import React, { useState, useMemo } from 'react';
import { useCarrito } from '../context/CarritoContext';
import './Ventas.css';

/**
 * Componente de resumen de venta / punto de venta.
 * - Muestra líneas del carrito
 * - Permite quitar líneas
 * - Calcula subtotal, descuento, IVA y total
 * - Captura método de pago, recibido y cambio
 * - Botón para imprimir ticket
 */
export default function CarritoResumen({ onImprimir, onVentaConfirmada, clienteNombre, clienteTelefono, notas }) {
  const {
    carrito,
    eliminarLinea,
    eliminarDelCarrito,
    actualizarCantidad,
    obtenerSubtotalSinDescuento,
    obtenerTotalDescuento,
    obtenerTotal,
    vaciarCarrito
  } = useCarrito();

  const [metodoPago, setMetodoPago] = useState('efectivo');
  const [recibido, setRecibido] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [mostrarModalImprimir, setMostrarModalImprimir] = useState(false);
  const [ventaRecientePayload, setVentaRecientePayload] = useState(null);
  const subtotal = obtenerSubtotalSinDescuento();
  const descuento = obtenerTotalDescuento();
  const totalSinIVA = obtenerTotal(); // precio ya con descuento
  const iva = 0; // IVA desactivado
  const total = totalSinIVA; // total final sin IVA

  const cambio = useMemo(() => {
    const valRec = parseFloat(recibido) || 0;
    if (metodoPago !== 'efectivo') return 0;
    return Math.max(0, +(valRec - total).toFixed(2));
  }, [recibido, total, metodoPago]);

  const formatear = n => n.toLocaleString('es-MX', { style:'currency', currency:'MXN' });

  const confirmarVenta = async () => {
    if (carrito.length === 0) return;
    setProcesando(true); setMensaje(null);
    try {
      const payload = {
        items: carrito.map(it => ({
          id_producto: it.id,
          cantidad: it.cantidad,
          precio_unitario: it.precioOriginal != null ? it.precioOriginal : it.precio,
          descuento_unitario: it.precioOriginal != null ? (it.precioOriginal - it.precio) : 0,
          // Incluir variant_id y metadata de variante si existen
          variant_id: it.variant_id || null,
          variant_color: it.variant_color || null,
          variant_talla: it.variant_talla || null,
          variant_sku: it.variant_sku || null
        })),
        subtotal_bruto: +subtotal.toFixed(2),
        descuento_total: +descuento.toFixed(2),
        subtotal_neto: +totalSinIVA.toFixed(2),
        iva: 0,
        total: +total.toFixed(2),
        metodo_pago: metodoPago,
        recibido: metodoPago==='efectivo'? (parseFloat(recibido)||0) : null,
        cambio: metodoPago==='efectivo'? cambio : null
      };
      const resp = await fetch('/api/ventas', {
        method:'POST',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify(payload)
      });
      if (resp.status === 409) {
        const data = await resp.json();
        setMensaje(data.error || 'Stock insuficiente');
        return;
      }
      if (!resp.ok) {
        let det='';
        try { const dataErr = await resp.json(); det = dataErr.detalle || dataErr.error || ''; } catch{}
        throw new Error(det || 'Error al confirmar venta');
      }
      const data = await resp.json();
      const payloadTicket = { carrito, subtotal, descuento, iva, total, metodoPago, recibido, cambio, folio: data.venta?.id_venta, clienteNombre, clienteTelefono, notas };
      setVentaRecientePayload(payloadTicket);
      setMensaje('Venta registrada folio #' + data.venta.id_venta);
      try {
        // Emitir evento global para que Ventas (que contiene el contexto audio) pueda reproducir beep success
        window.dispatchEvent(new CustomEvent('venta:success'));
      } catch {}
      // Ya NO imprimimos directo: pedimos confirmación
      setMostrarModalImprimir(true);
      onVentaConfirmada && onVentaConfirmada();
    } catch (e) {
      setMensaje(e.message);
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div className="resumen-venta">
      <h3>Resumen</h3>
      {carrito.length === 0 && <p>Carrito vacío.</p>}
      {carrito.length > 0 && (
        <table className="tabla-resumen">
          <thead>
            <tr>
              <th>Producto</th>
              <th>Cant</th>
              <th>Precio</th>
              <th>Desc</th>
              <th>Subtotal</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {carrito.map(item => {
              const base = item.precioOriginal != null ? item.precioOriginal : item.precio;
              const descUnit = item.precioOriginal != null ? (base - item.precio) : 0;
              return (
                <tr key={item.lineId || item.id}>
                  <td>{(() => { const base = (item.concepto || item.nombre || '—'); return base + (item.talla && !base.includes('(' + item.talla + ')') ? ` (${item.talla})` : ''); })()}</td>
                  <td>
                    <input
                      type="number"
                      min={1}
                      value={item.cantidad}
                      onChange={e => actualizarCantidad(item.id, parseInt(e.target.value)||1)}
                      className="cant-input"
                    />
                  </td>
                  <td>
                    {descUnit > 0 ? (
                      <span>
                        <span style={{textDecoration:'line-through', color:'#888', marginRight:6}}>{formatear(base)}</span>
                        <strong>{formatear(item.precio)}</strong>
                      </span>
                    ) : (
                      formatear(base)
                    )}
                  </td>
                  <td>{descUnit > 0 ? formatear(descUnit) : '—'}</td>
                  <td>{formatear(item.precio * item.cantidad)}</td>
                  <td>
                    <button
                      className="btn-eliminar-linea"
                      onClick={() => item.lineId ? eliminarLinea(item.lineId) : eliminarDelCarrito(item.id)}
                    >✕</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="totales-box">
        <div className="fila"><span>Subtotal bruto:</span><strong>{formatear(subtotal)}</strong></div>
        <div className="fila"><span>Descuento:</span><strong className="monto-desc">- {formatear(descuento)}</strong></div>
        <div className="fila"><span>Subtotal neto:</span><strong>{formatear(totalSinIVA)}</strong></div>
        <div className="fila total-final"><span>Total a pagar:</span><strong>{formatear(total)}</strong></div>
      </div>

      <div className="pago-box">
        <h4>Pago</h4>
        <div className="pago-row">
          <label style={{alignSelf:'flex-start'}}>Método:</label>
          <div className="metodo-pago-group" role="radiogroup" aria-label="Método de pago">
            {[
              {id:'efectivo', label:'Efectivo', icon:'💵'},
              {id:'tarjeta', label:'Tarjeta', icon:'💳'},
              {id:'transferencia', label:'Transfer.', icon:'🔄'},
              {id:'mixto', label:'Mixto', icon:'🧾'}
            ].map(opt => (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={metodoPago===opt.id}
                className={`metodo-btn metodo-${opt.id} ${metodoPago===opt.id? 'active':''}`}
                onClick={()=> setMetodoPago(opt.id)}
              >
                <span className="icon" aria-hidden>{opt.icon}</span>
                <span className="txt">{opt.label}</span>
              </button>
            ))}
          </div>
        </div>
        {metodoPago === 'efectivo' && (
          <div className="pago-row">
            <label>Recibido:</label>
            <input type="number" value={recibido} onChange={e => setRecibido(e.target.value)} />
            <span className="cambio-tag">Cambio: {formatear(cambio)}</span>
          </div>
        )}
        {mensaje && <div style={{ fontSize:'0.7rem', marginBottom:'6px', color: mensaje.startsWith('Venta')? '#0a6e2f':'#a10000' }}>{mensaje}</div>}
        <button
          disabled={carrito.length===0 || procesando}
          onClick={confirmarVenta}
          className="btn-imprimir-ticket"
          style={{ background:'#0d672a' }}
        >{procesando? 'Procesando...' : '✅ Confirmar Venta'}</button>
        <button
          disabled={carrito.length===0}
          onClick={() => onImprimir && onImprimir({ carrito, subtotal, descuento, iva, total, metodoPago, recibido, cambio, clienteNombre, clienteTelefono, notas })}
          className="btn-imprimir-ticket"
        >🧾 Imprimir Ticket</button>
      </div>

      {mostrarModalImprimir && (
        <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:3000}}>
          <div style={{background:'#fff', padding:'22px 26px 28px', borderRadius:'14px', width:'min(92%,420px)', boxShadow:'0 8px 28px rgba(0,0,0,0.3)', fontFamily:'system-ui, sans-serif'}}>
            <h3 style={{marginTop:0, marginBottom:'10px'}}>¿Imprimir ticket ahora?</h3>
            {ventaRecientePayload?.folio && <p style={{margin:'4px 0 14px'}}>Folio <strong>#{ventaRecientePayload.folio}</strong></p>}
            <p style={{fontSize:'.8rem', opacity:.75, marginTop:0}}>Puedes reimprimir después desde Reportes si eliges "No".</p>
            <div style={{display:'flex', gap:'12px', marginTop:'18px', justifyContent:'flex-end'}}>
              <button
                onClick={()=>{ vaciarCarrito(); setMostrarModalImprimir(false); }}
                style={{background:'#ccc', border:'none', padding:'8px 14px', borderRadius:8, cursor:'pointer', fontWeight:600}}
              >No</button>
              <button
                onClick={()=>{ if(onImprimir && ventaRecientePayload){ onImprimir(ventaRecientePayload); } vaciarCarrito(); setMostrarModalImprimir(false); }}
                style={{background:'#0d672a', color:'#fff', border:'none', padding:'8px 18px', borderRadius:8, cursor:'pointer', fontWeight:600}}
              >Sí, imprimir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
